import React, { useEffect, useMemo, useState } from 'react';
import { formatDate } from '../lib/utils';
import { Link } from 'react-router-dom';
import { ShieldCheck, AlertTriangle, Users, FileText, ArrowRight, Loader2 } from 'lucide-react';
import api from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';

/**
 * Data Quality Dashboard.
 *
 * Surfaces (procedure_code, payer_program) mismatches in existing patient
 * billing lines + Draft/Pending/Rejected claims so the user can clean
 * stale data before submitting to ODM CERT.
 */
const DataQualityPage = () => {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/data-quality/program-code-issues');
        setReport(res.data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const isClean = useMemo(
    () => !!report && report.grand_total_issues === 0,
    [report],
  );

  return (
    <div className="space-y-6 animate-fade-in" data-testid="data-quality-page">
      <header className="flex flex-col gap-1">
        <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
          Data Quality
        </h1>
        <p className="text-slate-500">
          Surfaces stale <span className="font-mono text-sm">(procedure_code, payer_program)</span>{' '}
          pairs that ODM would reject. Fix the flagged rows before your next CERT submission.
        </p>
      </header>

      {loading ? (
        <div className="py-24 flex items-center justify-center text-slate-500">
          <Loader2 className="h-5 w-5 animate-spin mr-2" />
          Scanning patients and claims…
        </div>
      ) : isClean ? (
        <Card className="border-emerald-200 bg-emerald-50/40">
          <CardContent className="py-12 flex flex-col items-center text-center">
            <ShieldCheck className="h-12 w-12 text-emerald-600 mb-3" />
            <div className="text-lg font-semibold text-emerald-900">All clean — no program/code mismatches</div>
            <div className="text-sm text-emerald-700 mt-1">
              Scanned {report.patients.total_scanned} patient(s) and {report.claims.total_scanned} claim(s).
            </div>
          </CardContent>
        </Card>
      ) : (
        <>
          <PatientIssuesCard data={report.patients} />
          <ClaimIssuesCard data={report.claims} />
        </>
      )}
    </div>
  );
};

const PatientIssuesCard = ({ data }) => {
  if (!data.issues.length) return null;
  return (
    <Card className="border-amber-200">
      <CardHeader className="flex flex-row items-center justify-between pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Users className="h-4 w-4 text-amber-600" />
          {data.flagged_count} patient{data.flagged_count === 1 ? '' : 's'} have{' '}
          {data.issue_count} bad billing line{data.issue_count === 1 ? '' : 's'}
          <span className="text-xs font-normal text-slate-500 ml-1">
            (of {data.total_scanned} scanned)
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="rounded-md border border-slate-200 divide-y divide-slate-100">
          {data.issues.map((p) => (
            <div key={p.patient_id} className="p-3" data-testid={`dq-patient-${p.patient_id}`}>
              <div className="flex items-center justify-between mb-2">
                <div>
                  <div className="font-semibold text-slate-900">{p.patient_name}</div>
                  <div className="text-xs text-slate-500 font-mono">Medicaid {p.medicaid_id_masked}</div>
                </div>
                <Link to={`/patients?focus=${p.patient_id}`}>
                  <Button size="sm" variant="outline" className="border-amber-300 text-amber-700 hover:bg-amber-50">
                    Fix in patient profile
                    <ArrowRight className="h-3.5 w-3.5 ml-1.5" />
                  </Button>
                </Link>
              </div>
              <div className="space-y-1">
                {p.bad_lines.map((ln) => (
                  <div
                    key={`${ln.line_index}:${ln.payer_program || ''}:${ln.procedure_code || ''}`}
                    className="text-xs flex items-center gap-2"
                  >
                    <AlertTriangle className="h-3 w-3 text-amber-600 shrink-0" />
                    <span className="text-slate-700">
                      Line {ln.line_index}: program{' '}
                      <span className="font-mono font-semibold text-rose-700">{ln.payer_program || '(blank)'}</span>{' '}
                      + code{' '}
                      <span className="font-mono font-semibold text-rose-700">{ln.procedure_code || '(blank)'}</span>
                      {ln.suggested_code ? (
                        <span className="text-emerald-700">
                          {' '}→ try{' '}
                          <span className="font-mono font-semibold">{ln.suggested_code}</span>
                        </span>
                      ) : (
                        <span className="text-slate-400"> (no obvious replacement — manual review)</span>
                      )}
                      {ln.is_legacy ? (
                        <span className="text-[10px] uppercase tracking-wider text-slate-400 ml-1">
                          legacy field
                        </span>
                      ) : null}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

const ClaimIssuesCard = ({ data }) => {
  if (!data.issues.length) return null;
  return (
    <Card className="border-rose-200">
      <CardHeader className="flex flex-row items-center justify-between pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <FileText className="h-4 w-4 text-rose-600" />
          {data.flagged_count} claim{data.flagged_count === 1 ? '' : 's'} carry invalid pairs
          <span className="text-xs font-normal text-slate-500 ml-1">
            (of {data.total_scanned} editable scanned — Draft / Pending / Rejected only)
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="rounded-md border border-slate-200 divide-y divide-slate-100">
          {data.issues.map((c) => (
            <div key={c.claim_id} className="p-3 flex items-center justify-between" data-testid={`dq-claim-${c.claim_id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="font-mono text-xs font-semibold text-slate-900">{c.claim_number}</span>
                  <span className="text-[10px] uppercase tracking-wider text-slate-500">{c.status}</span>
                </div>
                <div className="text-sm text-slate-700">{c.patient_name} · {formatDate(c.service_date)}</div>
                <div className="text-xs mt-0.5">
                  program{' '}
                  <span className="font-mono font-semibold text-rose-700">{c.payer_program || '(blank)'}</span>
                  {' '}+ code{' '}
                  <span className="font-mono font-semibold text-rose-700">{c.service_code}</span>
                  {c.suggested_code ? (
                    <span className="text-emerald-700 ml-1">
                      → try <span className="font-mono font-semibold">{c.suggested_code}</span>
                    </span>
                  ) : null}
                </div>
              </div>
              <Link to={`/claims?focus=${c.claim_id}`}>
                <Button size="sm" variant="outline" className="border-rose-300 text-rose-700 hover:bg-rose-50 shrink-0">
                  Open claim
                  <ArrowRight className="h-3.5 w-3.5 ml-1.5" />
                </Button>
              </Link>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export default DataQualityPage;
